import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Pane, Rectangle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useCounty } from "../lib/CountyContext";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { matchPerimeter } from "../lib/dedupe";
import CameraViewerModal from "./CameraViewerModal";
import MapBaseLayers from "./map/MapBaseLayers";
import RxBurnsLayer from "./map/RxBurnsLayer";
import MapLegend from "./map/MapLegend";
import PerimeterLayer from "./map/PerimeterLayer";
import CountyLinesLayer from "./map/CountyLinesLayer";
import SavedLocationsLayer from "./map/SavedLocationsLayer";
import CameraLayer from "./map/CameraLayer";
import IncidentLayer from "./map/IncidentLayer";
import NifcIncidentLayer from "./map/NifcIncidentLayer";
import InfoPinsLayer, { pinIcon } from "./map/InfoPinsLayer";
import ViirsHotspotsLayer from "./map/ViirsHotspotsLayer";
import DnrFiresLayer from "./map/DnrFiresLayer";
import WeatherAlertsLayer from "./map/WeatherAlertsLayer";
import SheriffEvacLayer from "./map/SheriffEvacLayer";
import EvacClosuresStrip from "./map/EvacClosuresStrip";
import RoadClosuresLayer from "./map/RoadClosuresLayer";
import WsdotClosuresLayer, { filterClosuresNearEvacs } from "./map/WsdotClosuresLayer";
import DotAlertsLayer from "./map/DotAlertsLayer";
import ScarRainLayer from "./map/ScarRainLayer";
import StreamGaugesLayer from "./map/StreamGaugesLayer";
import DownstreamLayer from "./map/DownstreamLayer";
import ZoomLabelScaler from "./map/ZoomLabelScaler";
import AircraftLayer from "./map/AircraftLayer";
import BcFiresLayer from "./map/BcFiresLayer";
import BcEvacsLayer from "./map/BcEvacsLayer";
import SmokeLayer from "./map/SmokeLayer";
import AqiLayer from "./map/AqiLayer";
import FieldPhotosLayer from "./map/FieldPhotosLayer";
import FollowedFiresLayer from "./map/FollowedFiresLayer";
import ConvergenceLayer from "./map/ConvergenceLayer";
import { fetchSmoke, fetchAqi, fetchFieldPhotos, fetchWindField, fetchWindFieldBBox, fetchLightning, fetchWxZones } from "../lib/api";
import FireZonesLayer from "./map/FireZonesLayer";
import MyEvacRouteButton, { MyRouteOverlay } from "./map/MyEvacRoute";
import WindLayer from "./map/WindLayer";
import LightningLayer from "./map/LightningLayer";
import SponsorRotator from "./SponsorRotator";
import RadarLayer from "./map/RadarLayer";
import { MapSearchBox, CaptureMap, SearchMarker } from "./map/MapSearchControl";
import { ScreenshotButton } from "./map/MapScreenshot";
import { MeasureLayer, MeasureButton } from "./map/MeasureTool";

const COUNTY_BOX_STYLE = {
  color: "#F2542D",
  weight: 1.5,
  opacity: 0.55,
  fillOpacity: 0.04,
  fillColor: "#F2542D",
  dashArray: "6 4",
};
const DEFAULT_CENTER = [48.45, -119.85];

function ZoomWatcher({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}

function ResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (map.attributionControl) map.attributionControl.setPosition("bottomleft");
  }, [map]);
  return null;
}

function DropPinOverlay({ pos, onChange }) {
  const map = useMap();
  useEffect(() => {
    if (!pos) {
      const c = map.getCenter();
      onChange([c.lat, c.lng]);
    }
  }, [pos, map, onChange]);
  if (!pos) return null;
  return (
    <Marker
      position={pos}
      icon={pinIcon("info")}
      draggable
      zIndexOffset={2000}
      eventHandlers={{
        dragend: (e) => {
          const ll = e.target.getLatLng();
          onChange([ll.lat, ll.lng]);
        },
      }}
    />
  );
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      // Never zoom the user OUT — keep their zoom if closer than 10
      map.flyTo(position, Math.max(map.getZoom(), 10), { duration: 0.8 });
    }
  }, [position, map]);
  return null;
}

function bboxToLeaflet(b) {
  if (!b) return null;
  return [[b.min_lat, b.min_lon], [b.max_lat, b.max_lon]];
}

function boundsCenter(bounds) {
  if (!bounds) return DEFAULT_CENTER;
  return [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];
}

export default function IncidentMap({
  incidents,
  activeId,
  onMarkerClick,
  focus,
  cameras = [],
  perimeters = [],
  evacuationZones = null,
  nifcIncidents = [],
  nifcAll = [],
  rxBurns = [],
  dnrBurnRequests = [],
  rxBurnUnits = null,
  viirsHotspots = [],
  viirsFrpHeatmap = false,
  dnrFires = [],
  weatherAlerts = [],
  sheriffEvacAlerts = [],
  evacFocus = null,
  closureFocus = null,
  wsdotFocus = null,
  passes = [],
  nifcHoverId = null,
  dnrHoverId = null,
  onIncidentMarkerHover,
  onNifcMarkerHover,
  onDnrMarkerHover,
  roadClosures = [],
  wsdotAlerts = [],
  aircraft = [],
  bcFires = [],
  bcEvacs = [],
  followedFires = [],
  showFollowed = true,
  onToggleFollowed,
  convergences = [],
  allIncidents = [],
  infoPins = [],
  canManagePins = false,
  onQuickDebris,
  draggablePins = false,
  onMovePin,
  dropPinActive = false,
  onDropPinConfirm,
  onDropPinCancel,
  onAddPin,
  onEditPin,
  onDeletePin,
}) {
  const { current } = useCounty();
  const { locations } = useSavedLocations();
  const [activeCamera, setActiveCamera] = useState(null);
  // NWS polygons start hidden — users opt in via the Layers dropdown.
  const [showWeatherAlerts, setShowWeatherAlerts] = useState(false);
  const [showWxZones, setShowWxZones] = useState(false);
  const [wxZones, setWxZones] = useState(null);
  const [myRoute, setMyRoute] = useState(null);
  const [showSmoke, setShowSmoke] = useState(false);
  const [smoke, setSmoke] = useState(null);
  const [showAqi, setShowAqi] = useState(false);
  const [aqi, setAqi] = useState(null);
  const [showFieldPhotos, setShowFieldPhotos] = useState(true);
  const [fieldPhotos, setFieldPhotos] = useState([]);
  const [layersOpen, setLayersOpen] = useState(false);
  const [showWind, setShowWind] = useState(false);

  // Auto-enable Live Wind once when a Red Flag Warning is active
  const windAutoRef = useRef(false);
  const redFlagActive = useMemo(
    () => (weatherAlerts || []).some((a) => /red flag/i.test(a.event || a.headline || "")),
    [weatherAlerts]
  );
  useEffect(() => {
    if (redFlagActive && !windAutoRef.current) {
      windAutoRef.current = true;
      setShowWind(true);
    }
  }, [redFlagActive]);
  const [windField, setWindField] = useState(null);
  const [showRadar, setShowRadar] = useState(false);
  const [showLightning, setShowLightning] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("lightning")
  );
  const [lightning, setLightning] = useState([]);
  const [lightningHours, setLightningHours] = useState(1);
  const [popupEvac, setPopupEvac] = useState(null);
  const [stripFocus, setStripFocus] = useState(null);
  const [searchMap, setSearchMap] = useState(null);
  const [dropPos, setDropPos] = useState(null);
  useEffect(() => {
    if (!dropPinActive) setDropPos(null);
  }, [dropPinActive]);
  const [searchMarker, setSearchMarker] = useState(null);
  const [zoneFocus, setZoneFocus] = useState(null);
  const [measureActive, setMeasureActive] = useState(false);
  const [measureTotal, setMeasureTotal] = useState(null);
  const [measureDrawing, setMeasureDrawing] = useState(false);
  const [measureFinishTick, setMeasureFinishTick] = useState(0);
  const [showFireLabels, setShowFireLabels] = useState(
    () => localStorage.getItem("fw_fire_labels") !== "0"
  );
  // Ground-truth layers: on by default (safety), user choice remembered.
  const [showEvacSigns, setShowEvacSigns] = useState(
    () => localStorage.getItem("fw_layer_evac_signs") !== "0"
  );
  const [showClosureSigns, setShowClosureSigns] = useState(
    () => localStorage.getItem("fw_layer_closures") !== "0"
  );
  const [showPerimeters, setShowPerimeters] = useState(
    () => localStorage.getItem("fw_layer_perimeters") !== "0"
  );
  const [showInfoPins, setShowInfoPins] = useState(
    () => localStorage.getItem("fw_layer_info_pins") !== "0"
  );
  const [showDotAlerts, setShowDotAlerts] = useState(
    () => localStorage.getItem("fw_layer_dot_alerts") !== "0"
  );
  const [showScarRain, setShowScarRain] = useState(
    () => localStorage.getItem("fw_layer_scar_rain") !== "0"
  );
  // Prescribed burns: OFF by default — informational, opt-in.
  const [showRxBurns, setShowRxBurns] = useState(
    () => localStorage.getItem("fw_layer_rx_burns") === "1"
  );
  const [showGauges, setShowGauges] = useState(
    () => localStorage.getItem("fw_layer_gauges") !== "0"
  );
  const [showDownstream, setShowDownstream] = useState(
    () => localStorage.getItem("fw_layer_downstream") !== "0"
  );
  const [showCountyLines, setShowCountyLines] = useState(
    () => localStorage.getItem("fw_layer_county_lines") !== "0"
  );
  const [mapZoom, setMapZoom] = useState(8);
  // eslint-disable-next-line no-unused-vars
  const detailZoom = mapZoom >= 9.5;

  useEffect(() => {
    if (!measureActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMeasureActive(false);
        setMeasureTotal(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [measureActive]);

  useEffect(() => {
    fetchFieldPhotos().then(setFieldPhotos).catch(() => {});
  }, []);

  useEffect(() => {
    if (showSmoke && !smoke) {
      fetchSmoke().then(setSmoke).catch(() => {});
    }
  }, [showSmoke, smoke]);

  useEffect(() => {
    if (showAqi && !aqi) {
      fetchAqi().then(setAqi).catch(() => {});
    }
  }, [showAqi, aqi]);

  useEffect(() => {
    if (showWxZones && !wxZones) {
      fetchWxZones().then(setWxZones).catch(() => {});
    }
  }, [showWxZones, wxZones]);

  useEffect(() => {
    if (!showWind) return;
    const load = () => {
      const b = current?.bounds;
      Promise.all([
        fetchWindField(current?.slug),
        b
          ? fetchWindFieldBBox({ min_lat: b.min_lat - 1.2, max_lat: b.max_lat + 1.2, min_lon: b.min_lon - 1.2, max_lon: b.max_lon + 1.2 })
          : Promise.resolve([]),
      ])
        .then(([inCounty, around]) => {
          const seen = new Set((inCounty || []).map((p) => p.station));
          setWindField([...(inCounty || []), ...(around || []).filter((p) => !seen.has(p.station))]);
        })
        .catch(() => {});
    };
    load();
    // silent refresh so arrows + pinned popups update with the newest observations
    const id = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWind, current]);

  useEffect(() => {
    if (!showLightning) return;
    const load = () =>
      fetchLightning(60, lightningHours).then((d) => setLightning(d.strikes || [])).catch(() => {});
    load();
    const id = setInterval(load, lightningHours > 1 ? 120000 : 30000);
    return () => clearInterval(id);
  }, [showLightning, lightningHours]);
  const bounds = bboxToLeaflet(current?.bounds);
  const center = boundsCenter(bounds);

  const visibleIncidents = useMemo(
    () => incidents.filter((i) => i.latitude != null && i.longitude != null),
    [incidents]
  );
  const perimetersWithGeom = useMemo(
    () => perimeters.filter((p) => p.geometry),
    [perimeters]
  );

  // Fire hovered/selected anywhere (feed card or marker) → flash its mapped
  // perimeter yellow so the burn area jumps out.
  const flashPerim = useMemo(() => {
    const rec =
      (activeId && incidents?.find((i) => i.id === activeId)) ||
      (dnrHoverId && dnrFires.find((f) => f.id === dnrHoverId)) ||
      (nifcHoverId && nifcIncidents.find((n) => n.id === nifcHoverId)) ||
      null;
    if (!rec) return null;
    return matchPerimeter(rec, perimetersWithGeom);
  }, [activeId, incidents, dnrHoverId, dnrFires, nifcHoverId, nifcIncidents, perimetersWithGeom]);

  // Every evac level (1/2/3) stays visible at all zoom levels — canvas
  // rendering keeps it smooth. A fly-to focus or toggle-off still applies.
  const visibleEvacAlerts = useMemo(() => {
    if (!showEvacSigns) {
      const focus = evacFocus || zoneFocus;
      return focus ? sheriffEvacAlerts.filter((a) => a.id === focus.id) : [];
    }
    return sheriffEvacAlerts;
  }, [showEvacSigns, sheriffEvacAlerts, evacFocus, zoneFocus]);
  const closuresVisible = showClosureSigns;

  return (
    <div className={`absolute inset-0${showFireLabels ? "" : " hide-fire-labels"}`} data-testid="incident-map">
      <MapContainer
        center={center}
        zoom={8}
        bounds={bounds ?? undefined}
        scrollWheelZoom
        zoomControl
        zoomSnap={0.5}
        zoomDelta={0.5}
        wheelPxPerZoomLevel={90}
        className="h-full w-full"
        style={{ background: "#0F1311" }}
        key={current?.slug || "default"}
      >
        <ResizeHandler />
        <ZoomWatcher onZoom={setMapZoom} />
        {/* Fire icons ride above every other marker/overlay layer (markerPane
            is 600, tooltips 650, popups 700) so they always take the click. */}
        <Pane name="fire-icons" style={{ zIndex: 660 }} />
        {showCountyLines && <CountyLinesLayer />}
        {focus && <FlyTo position={focus} />}
        {stripFocus && <FlyTo position={stripFocus} />}

        <MapBaseLayers />
        <CaptureMap onMap={setSearchMap} />
        <SearchMarker marker={searchMarker} />
        {measureActive && <MeasureLayer key={String(measureActive)} onTotal={setMeasureTotal} onDrawing={setMeasureDrawing} finishTick={measureFinishTick} />}

        {bounds && <Rectangle bounds={bounds} pathOptions={COUNTY_BOX_STYLE} />}

        <SheriffEvacLayer alerts={visibleEvacAlerts} focusEvac={evacFocus || zoneFocus} onPopupEvac={setPopupEvac} />
        {closuresVisible && <RoadClosuresLayer closures={roadClosures} focusClosure={closureFocus} />}
        {closuresVisible && !showDotAlerts && <WsdotClosuresLayer alerts={wsdotAlerts} evacAlerts={sheriffEvacAlerts} evacuationZones={evacuationZones} focusWsdot={wsdotFocus} />}
        {showDotAlerts && <DotAlertsLayer alerts={wsdotAlerts} passes={passes} focusWsdot={wsdotFocus} />}
        {showScarRain && <ScarRainLayer perimeters={perimetersWithGeom} />}
        {showGauges && <StreamGaugesLayer />}
        {showDownstream && <DownstreamLayer />}
        <ZoomLabelScaler />
        <AircraftLayer aircraft={aircraft} />
        <BcFiresLayer fires={bcFires} />
        <BcEvacsLayer evacs={bcEvacs} />
        {showWeatherAlerts && <WeatherAlertsLayer alerts={weatherAlerts} />}
        {showSmoke && <SmokeLayer smoke={smoke} />}
        {showRadar && <RadarLayer />}
        {showLightning && (
          <LightningLayer
            strikes={lightning}
            fires={[
              ...(allIncidents.length ? allIncidents : incidents),
              ...nifcIncidents,
              ...dnrFires,
            ].map((f) => ({
              name: f.name || f.incident_name,
              latitude: f.latitude ?? f.lat,
              longitude: f.longitude ?? f.lon,
            }))}
          />
        )}
        {showWind && windField && <WindLayer points={windField} />}
        {showWxZones && wxZones && <FireZonesLayer zones={wxZones} alerts={weatherAlerts} />}
        {myRoute && <MyRouteOverlay route={myRoute} />}
        {showAqi && <AqiLayer aqi={aqi} />}
        {showFieldPhotos && <FieldPhotosLayer photos={fieldPhotos} />}
        {showPerimeters && (
          <PerimeterLayer perimeters={perimetersWithGeom} flashId={flashPerim?.id} linkIncidents={allIncidents.length ? allIncidents : incidents} />
        )}
        <ViirsHotspotsLayer hotspots={viirsHotspots} frpHeatmap={viirsFrpHeatmap} />
        <DnrFiresLayer fires={dnrFires} activeId={dnrHoverId} onMarkerHover={onDnrMarkerHover} wildcadIncidents={allIncidents.length ? allIncidents : incidents} perimeters={perimeters} />
        <NifcIncidentLayer incidents={nifcIncidents} activeId={nifcHoverId} onMarkerHover={onNifcMarkerHover} wildcadIncidents={incidents} linkIncidents={allIncidents.length ? allIncidents : incidents} dnrFires={dnrFires} perimeters={perimeters} />
        {showRxBurns && <RxBurnsLayer burns={rxBurns} dnrBurns={dnrBurnRequests} units={rxBurnUnits} />}
        <SavedLocationsLayer locations={locations} />
        {/* Blue information pins are their own toggleable layer; all other
            pin types stay always-visible. */}
        <InfoPinsLayer
          pins={infoPins.filter((p) => (p.icon || "info") !== "info")}
          canManage={canManagePins}
          draggable={draggablePins}
          onMove={onMovePin}
          onEdit={onEditPin}
          onDelete={onDeletePin}
        />
        {showInfoPins && (
          <InfoPinsLayer
            pins={infoPins.filter((p) => (p.icon || "info") === "info")}
            canManage={canManagePins}
            draggable={draggablePins}
            onMove={onMovePin}
            onEdit={onEditPin}
            onDelete={onDeletePin}
          />
        )}
        {dropPinActive && <DropPinOverlay pos={dropPos} onChange={setDropPos} />}
        {showFollowed && <FollowedFiresLayer fires={followedFires} />}
        <ConvergenceLayer pairs={convergences} />
        <CameraLayer cameras={cameras} onOpenCamera={setActiveCamera} />
        <IncidentLayer
          incidents={visibleIncidents}
          activeId={activeId}
          onMarkerClick={onMarkerClick}
          onMarkerHover={onIncidentMarkerHover}
          nifcIncidents={nifcAll}
        />
      </MapContainer>

      <SponsorRotator variant="map" />

      {dropPinActive && (
        <div
          data-testid="drop-pin-bar"
          className="absolute top-14 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-2 bg-tac-bg/90 backdrop-blur-xl border border-tac-border rounded-sm px-3 py-2"
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-tac-text whitespace-nowrap">
            Drag the pin into place
          </span>
          <button
            type="button"
            data-testid="drop-pin-confirm"
            disabled={!dropPos}
            onClick={() => dropPos && onDropPinConfirm?.(dropPos)}
            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm text-white disabled:opacity-50"
            style={{ background: "#3B9DF2" }}
          >
            ✓ Place here
          </button>
          <button
            type="button"
            data-testid="drop-pin-cancel"
            onClick={() => onDropPinCancel?.()}
            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-tac-border text-tac-dim hover:text-tac-text"
          >
            Cancel
          </button>
        </div>
      )}

      <MapLegend
        cameras={cameras.length}
        perimeters={perimeters.length}
        locations={locations.length}
        nifc={nifcIncidents.length}
        viirs={viirsHotspots.length}
        dnr={dnrFires.length}
        evacs={sheriffEvacAlerts.length}
        roadClosures={roadClosures.length}
        wsdotClosures={filterClosuresNearEvacs(wsdotAlerts, sheriffEvacAlerts, evacuationZones).length}
        aircraft={aircraft.length}
        countyName={current?.name}
      />

      <div className="absolute top-3 left-14 right-14 sm:right-auto z-[1000]">
        <div className="flex flex-wrap items-start gap-2">
          <button
            type="button"
            data-testid="map-layers-button"
            onClick={() => setLayersOpen((v) => !v)}
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-tac-border text-tac-text hover:border-tac-borderBright transition-colors whitespace-nowrap"
          >
            Layers {layersOpen ? "▴" : "▾"}
          </button>
          {canManagePins && (
            <button
              type="button"
              data-testid="add-info-pin-btn"
              onClick={onAddPin}
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-tac-border text-tac-text hover:border-tac-borderBright transition-colors whitespace-nowrap"
            >
              + Info Pin
            </button>
          )}
          {canManagePins && onQuickDebris && (
            <button
              type="button"
              data-testid="quick-debris-btn"
              onClick={onQuickDebris}
              title="One-tap: report debris/mudflow on road at your GPS location"
              className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 backdrop-blur-xl border-[#8B5E3C]/60 text-[#C89B7B] hover:border-[#8B5E3C] transition-colors whitespace-nowrap"
            >
              🪨 Debris
            </button>
          )}
          {searchMap && (
            <MapSearchBox
              map={searchMap}
              onMarker={setSearchMarker}
              evacAlerts={sheriffEvacAlerts}
              onZone={(a) => setZoneFocus({ id: a.id, ts: Date.now() })}
            />
          )}
          <MyEvacRouteButton map={searchMap} onRoute={setMyRoute} />
          <ScreenshotButton map={searchMap} />
          <MeasureButton
            active={measureActive}
            total={measureTotal}
            drawing={measureDrawing}
            onFinish={() => setMeasureFinishTick((t) => t + 1)}
            onToggle={() => {
              setMeasureActive((v) => !v);
              setMeasureTotal(null);
              setMeasureDrawing(false);
            }}
          />
          <button
            type="button"
            data-testid="fire-labels-toggle"
            title="Show or hide fire name labels on the map"
            onClick={() =>
              setShowFireLabels((v) => {
                localStorage.setItem("fw_fire_labels", v ? "0" : "1");
                return !v;
              })
            }
            className={`hidden sm:block px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-tac-bg/85 backdrop-blur-xl transition-colors whitespace-nowrap ${
              showFireLabels
                ? "border-tac-primary text-tac-primary"
                : "border-tac-border text-tac-dim hover:text-tac-text hover:border-tac-borderBright"
            }`}
          >
            Labels {showFireLabels ? "On" : "Off"}
          </button>
        </div>
        {layersOpen && (
          <div className="mt-1.5 w-44 bg-tac-bg/90 backdrop-blur-xl border border-tac-border rounded-sm p-2 space-y-1.5">
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer sm:hidden"
              data-testid="fire-labels-layer-toggle"
            >
              <input
                type="checkbox"
                checked={showFireLabels}
                onChange={() =>
                  setShowFireLabels((v) => {
                    localStorage.setItem("fw_fire_labels", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#FF7A2F]"
              />
              Fire Labels
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="followed-fires-toggle"
            >
              <input
                type="checkbox"
                checked={showFollowed}
                onChange={() => onToggleFollowed?.()}
                className="accent-[#F2542D]"
              />
              Followed Fires{followedFires.length ? ` (${followedFires.length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="perimeters-toggle"
            >
              <input
                type="checkbox"
                checked={showPerimeters}
                onChange={() =>
                  setShowPerimeters((v) => {
                    localStorage.setItem("fw_layer_perimeters", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#F2542D]"
              />
              Fire Perimeters{perimetersWithGeom.length ? ` (${perimetersWithGeom.length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="info-pins-toggle"
            >
              <input
                type="checkbox"
                checked={showInfoPins}
                onChange={() =>
                  setShowInfoPins((v) => {
                    localStorage.setItem("fw_layer_info_pins", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#3B9DF2]"
              />
              Info Pins{infoPins.filter((p) => (p.icon || "info") === "info").length ? ` (${infoPins.filter((p) => (p.icon || "info") === "info").length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="evac-signs-toggle"
            >
              <input
                type="checkbox"
                checked={showEvacSigns}
                onChange={() =>
                  setShowEvacSigns((v) => {
                    localStorage.setItem("fw_layer_evac_signs", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#E63946]"
              />
              Evac Signs{sheriffEvacAlerts.length ? ` (${sheriffEvacAlerts.length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="closure-signs-toggle"
            >
              <input
                type="checkbox"
                checked={showClosureSigns}
                onChange={() =>
                  setShowClosureSigns((v) => {
                    localStorage.setItem("fw_layer_closures", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#FFB347]"
              />
              Road Closures
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="dot-alerts-toggle"
            >
              <input
                type="checkbox"
                checked={showDotAlerts}
                onChange={() =>
                  setShowDotAlerts((v) => {
                    localStorage.setItem("fw_layer_dot_alerts", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#F77F00]"
              />
              DOT Alerts{wsdotAlerts.length + passes.length ? ` (${wsdotAlerts.filter((a) => a.start_lat != null).length + passes.length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="rx-burns-toggle"
            >
              <input
                type="checkbox"
                checked={showRxBurns}
                onChange={() =>
                  setShowRxBurns((v) => {
                    localStorage.setItem("fw_layer_rx_burns", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#9F7AEA]"
              />
              Rx Burns{rxBurns.length + dnrBurnRequests.length ? ` (${rxBurns.length + dnrBurnRequests.length})` : ""}
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="scar-rain-toggle"
            >
              <input
                type="checkbox"
                checked={showScarRain}
                onChange={() =>
                  setShowScarRain((v) => {
                    localStorage.setItem("fw_layer_scar_rain", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#4A9BD1]"
              />
              Scar Rain 48h
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="gauges-toggle"
            >
              <input
                type="checkbox"
                checked={showGauges}
                onChange={() =>
                  setShowGauges((v) => {
                    localStorage.setItem("fw_layer_gauges", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#4A9BD1]"
              />
              Stream Gauges
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="downstream-toggle"
            >
              <input
                type="checkbox"
                checked={showDownstream}
                onChange={() =>
                  setShowDownstream((v) => {
                    localStorage.setItem("fw_layer_downstream", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#4A9BD1]"
              />
              Downstream Paths
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="county-lines-toggle"
            >
              <input
                type="checkbox"
                checked={showCountyLines}
                onChange={() =>
                  setShowCountyLines((v) => {
                    localStorage.setItem("fw_layer_county_lines", v ? "0" : "1");
                    return !v;
                  })
                }
                className="accent-[#9AA5B1]"
              />
              County Lines
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="weather-alerts-toggle"
            >
              <input
                type="checkbox"
                checked={showWeatherAlerts}
                onChange={() => setShowWeatherAlerts((v) => !v)}
                className="accent-[#E63946]"
                disabled={!weatherAlerts.some((a) => a.geometry)}
              />
              NWS Wx Alerts
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="fire-zones-toggle"
            >
              <input
                type="checkbox"
                checked={showWxZones}
                onChange={() => setShowWxZones((v) => !v)}
                className="accent-[#94A3B8]"
              />
              Fire Wx Zones
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="smoke-toggle"
            >
              <input
                type="checkbox"
                checked={showSmoke}
                onChange={() => setShowSmoke((v) => !v)}
                className="accent-[#6B7280]"
              />
              Smoke (NOAA)
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="aqi-toggle"
            >
              <input
                type="checkbox"
                checked={showAqi}
                onChange={() => setShowAqi((v) => !v)}
                className="accent-[#00E400]"
              />
              AQI Monitors
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="wind-toggle"
            >
              <input
                type="checkbox"
                checked={showWind}
                onChange={() => setShowWind((v) => !v)}
                className="accent-[#8FA99B]"
              />
              Live Wind
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="radar-toggle"
            >
              <input
                type="checkbox"
                checked={showRadar}
                onChange={() => setShowRadar((v) => !v)}
                className="accent-[#4CC9F0]"
              />
              Rain Radar
            </label>
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="lightning-toggle-layer"
            >
              <input
                type="checkbox"
                checked={showLightning}
                onChange={() => setShowLightning((v) => !v)}
                className="accent-[#FFD60A]"
              />
              Lightning{showLightning && lightning.length ? ` (${lightning.length})` : ""}
            </label>
            {showLightning && (
              <div className="flex items-center gap-1 pl-5" data-testid="lightning-range-picker">
                {[1, 24, 48, 72].map((h) => (
                  <button
                    key={h}
                    type="button"
                    data-testid={`lightning-range-${h}h`}
                    onClick={() => setLightningHours(h)}
                    className={`px-1.5 py-0.5 text-[9px] font-bold rounded-sm border transition-colors ${
                      lightningHours === h
                        ? "bg-[#FFD60A]/20 border-[#FFD60A] text-[#FFD60A]"
                        : "border-tac-border text-tac-dim hover:text-tac-text"
                    }`}
                  >
                    {h}H
                  </button>
                ))}
              </div>
            )}
            <label
              className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-tac-text cursor-pointer"
              data-testid="field-photos-toggle"
            >
              <input
                type="checkbox"
                checked={showFieldPhotos}
                onChange={() => setShowFieldPhotos((v) => !v)}
                className="accent-[#E9C46A]"
              />
              Field Photos{fieldPhotos.length ? ` (${fieldPhotos.length})` : ""}
            </label>
            <div className="pt-1.5 border-t border-tac-border/60">
              <ScreenshotButton map={searchMap} variant="row" />
            </div>
          </div>
        )}
      </div>

      {activeCamera && (
        <CameraViewerModal camera={activeCamera} onClose={() => setActiveCamera(null)} />
      )}

      <EvacClosuresStrip
        evac={popupEvac}
        closures={roadClosures}
        wsdotAlerts={wsdotAlerts}
        onRoadClick={(lat, lon) => setStripFocus([lat, lon])}
      />
    </div>
  );
}

if (typeof window !== "undefined" && L && L.Icon && L.Icon.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
}

